import React from 'react';

import { Button, Confirm } from 'semantic';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import InspectObject from 'components/modals/InspectObject';
import { PaymentTerminal } from 'subdomains/cpo/types/payment-terminals';
import { PAYMENT_TERMINALS_V1_API_URL } from 'subdomains/cpo/router/api';
import { request } from 'utils/api';
import { useToast } from 'components/Toast/useToast';
import Modal from '../../../../../screens/Location/PaymentTerminals/Modal';
import { buildConnectors } from 'screens/Location/PaymentTerminals/Table';
import { EnrichedPaymentTerminal } from 'subdomains/cpo/types/payment-terminals';
import { Location as SharedLocation } from 'types/location';
import { useUser } from 'contexts/user';
import {
  hasPaymentTerminalPermissions,
  hasPaymentTerminalPlatformFeature,
} from 'screens/Location/PaymentTerminals/utils';

type Props = {
  paymentTerminal: EnrichedPaymentTerminal;
  onDeleted?: () => void;
  onUpdated?: () => void;
};

export function PaymentTerminalActions({
  paymentTerminal,
  onDeleted,
  onUpdated,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const { user, provider } = useUser();
  const { hasFeature, loading: hasFeatureLoading } =
    hasPaymentTerminalPlatformFeature();

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await request({
        path: `${PAYMENT_TERMINALS_V1_API_URL}/${id}`,
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      onDeleted?.();
      toast.success(
        t(
          'paymentTerminalModal.deleteSuccess',
          'Payment terminal deleted successfully'
        )
      );
    },
    onError: () => {
      toast.error(
        t(
          'paymentTerminalModal.deleteError',
          'Failed to delete payment terminal'
        )
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (paymentTerminal: PaymentTerminal) => {
      await request({
        path: `${PAYMENT_TERMINALS_V1_API_URL}/${paymentTerminal.id}`,
        method: 'PATCH',
        body: paymentTerminal,
      });
    },
    onSuccess: () => {
      onUpdated?.();
      toast.success(
        t(
          'paymentTerminalModal.updateSuccess',
          'Payment terminal updated successfully'
        )
      );
    },
  });

  if (!user || !provider || hasFeatureLoading) return <></>;

  const canWrite =
    hasFeature && hasPaymentTerminalPermissions(user, 'write', provider);

  const connectors = buildConnectors(
    paymentTerminal.location as SharedLocation,
    [paymentTerminal]
  );

  const handleUpdate = async (paymentTerminal: PaymentTerminal) => {
    try {
      await updateMutation.mutateAsync(paymentTerminal);
    } catch (error) {
      return error;
    }
  };

  return (
    <>
      <InspectObject
        name={t('paymentTerminalModal.inspect', 'Payment Terminal')}
        data={paymentTerminal}
        trigger={<Button basic title="Inspect" icon="file-code" />}
      />
      {canWrite && (
        <>
          <Modal
            size="small"
            locations={[location]}
            paymentTerminal={paymentTerminal}
            connectors={connectors}
            submit={handleUpdate}
            loading={updateMutation.isPending}
            trigger={<Button basic icon="edit" as="button" />}
          />

          <Confirm
            header={t(
              'locationPaymentTerminal.deleteConfirmTitle',
              'Delete Payment Terminal'
            )}
            content={t(
              'locationPaymentTerminal.deleteConfirmContent',
              'Are you sure you want to delete this payment terminal?'
            )}
            trigger={<Button basic icon="trash" as="button" />}
            onConfirm={() => deleteMutation.mutate(paymentTerminal.id)}
          />
        </>
      )}
    </>
  );
}
